import {
  capitaliseFirstLetter,
  sentenceCase,
} from '@payaca/utilities/stringUtilities';
import { Nullish } from '@payaca/utilities/types';

const ACRONYMS = new Set(['dno', 'mpan']);

const ABBREVIATION_EXPANSIONS = new Map([
  ['ex', 'export'],
  ['cx', 'commissioning'],
]);

const TOKEN_PARTS = /^(\W*)([A-Za-z]+)(\W*)$/;

/**
 * ENA sends commissioning statuses as free text in its own Title Case, so this
 * re-cases them as a sentence.
 */
export function prettifyCommissioningStatus(
  commissioningStatus: Nullish<string>
): string {
  if (!commissioningStatus) {
    return 'Submitted (awaiting approval)';
  }

  const tokens = commissioningStatus
    .trim()
    .split(/\s+/)
    .map((token) => {
      const [, leading = '', core = token, trailing = ''] =
        TOKEN_PARTS.exec(token) ?? [];

      return { leading, core: expandEnaWord(core), trailing };
    });

  const sentenceCasedCores = sentenceCase(
    tokens.map(({ core }) => core).join(' ')
  ).split(' ');

  const prettified = tokens
    .map(
      ({ leading, core, trailing }, index) =>
        `${leading}${sentenceCasedCores[index] ?? core}${trailing}`
    )
    .join(' ');

  return capitaliseFirstAlphabeticCharacter(prettified);
}

function expandEnaWord(word: string): string {
  const lowercaseWord = word.toLowerCase();

  if (ACRONYMS.has(lowercaseWord)) {
    return word.toUpperCase();
  }

  return ABBREVIATION_EXPANSIONS.get(lowercaseWord) ?? word;
}

function capitaliseFirstAlphabeticCharacter(text: string): string {
  const firstLetterIndex = text.search(/[A-Za-z]/);

  if (firstLetterIndex === -1) {
    return text;
  }

  return (
    text.slice(0, firstLetterIndex) +
    capitaliseFirstLetter(text.slice(firstLetterIndex))
  );
}
